import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [ref1, inView1] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ref2, inView2] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: 'Adres',
      content: 'Selçuklu Cd. No:49/1, Erbaa, Tokat / Türkiye',
    },
    {
      icon: 'ri-phone-line',
      title: 'Telefon',
      content: '+90 (356) 502 81 19',
    },
    {
      icon: 'ri-mail-line',
      title: 'E-posta',
      content: 'info@anadolupetambalaj.com',
    },
    {
      icon: 'ri-time-line',
      title: 'Çalışma Saatleri',
      content: 'Pazartesi - Cuma: 08:00 - 18:00',
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5i0hcrgekujqjrtsrqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white font-poppins">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 bg-[#faf8f5]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-teal text-xs sm:text-sm font-semibold uppercase tracking-wider">/İLETİŞİM</span>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-navy mt-4 sm:mt-6 mb-6 sm:mb-8 leading-tight">
              Bizimle<br />İletişime Geçin
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-700 max-w-[720px] leading-relaxed">
              <strong className="font-semibold text-navy">Ambalaj ihtiyaçlarınız</strong> için bizimle iletişime geçin. Uzman ekibimiz size en uygun <strong className="font-semibold text-navy">çözümleri sunmak</strong> için hazır. <strong className="font-semibold text-navy">Teklif almak</strong> veya detaylı bilgi için formu doldurun.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section ref={ref1} className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView1 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl lg:rounded-2xl p-6 sm:p-8 text-center hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <i className={`${info.icon} text-teal text-4xl sm:text-5xl`} />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-navy mb-2 sm:mb-3">{info.title}</h3>
                <p className="text-gray-700 text-sm sm:text-[15px] leading-relaxed">{info.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section ref={ref2} className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={inView2 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-4 sm:mb-6">Teklif Alın</h2>
              <p className="text-gray-700 text-sm sm:text-[15px] mb-6 sm:mb-8 leading-relaxed">
                Formu doldurun, en kısa sürede size dönüş yapalım. Ambalaj ihtiyaçlarınız için özel çözümler sunuyoruz.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-readdy-form id="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-navy mb-2">
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none transition-all text-sm"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-xs sm:text-sm font-semibold text-navy mb-2">
                      Firma Adı *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none transition-all text-sm"
                      placeholder="Firma adınız"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-navy mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none transition-all text-sm"
                      placeholder="ornek@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs sm:text-sm font-semibold text-navy mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none transition-all text-sm"
                      placeholder="+90 5XX XXX XX XX"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="product" className="block text-xs sm:text-sm font-semibold text-navy mb-2">
                    İlgilendiğiniz Ürün *
                  </label>
                  <select
                    id="product"
                    name="product"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Ürün seçiniz</option>
                    <option value="PET Şişe">PET Şişe</option>
                    <option value="PET Kavanoz">PET Kavanoz</option>
                    <option value="PET Preform">PET Preform</option>
                    <option value="PP Kapak">PP Kapak</option>
                    <option value="Tümü">Tümü</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-navy mb-2">
                    Mesajınız *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none transition-all text-sm resize-none"
                    placeholder="İhtiyaçlarınızı ve sorularınızı detaylı olarak yazınız (maksimum 500 karakter)"
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full bg-teal text-white font-semibold py-3 sm:py-4 rounded-lg hover:bg-teal/90 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                >
                  {formStatus === 'loading' ? 'Gönderiliyor...' : 'Teklif Talep Et'}
                </button>

                {formStatus === 'success' && (
                  <div className="bg-green/10 border border-green text-green px-4 py-3 rounded-lg text-sm">
                    Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg text-sm">
                    Bir hata oluştu. Lütfen tekrar deneyin veya telefon ile iletişime geçin.
                  </div>
                )}
              </form>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView2 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="w-full h-[400px] sm:h-[500px] lg:h-[700px] rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d36.5945740!3d40.6812250!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDQwJzUyLjQiTiAzNsKwMzUnNDAuNSJF!5e0!3m2!1str!2str!4v1700000000000!5m2!1str!2str"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="AnadoluPet Ambalaj Konum"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-navy to-teal">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 sm:mb-6">
              Ambalaj Çözümleriniz İçin Hazırız
            </h2>
            <p className="text-white/90 text-sm sm:text-base lg:text-lg mb-8 sm:mb-10 max-w-[700px] mx-auto px-4">
              Yüksek kaliteli PET ambalaj ürünleri ve profesyonel hizmet için bizi arayın
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <a
                href="tel:+903565028119"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-white text-navy font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-phone-line text-lg sm:text-xl" />
                Hemen Arayın
              </a>
              <a
                href="mailto:info@anadolupetambalaj.com"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-lg border border-white/20 hover:bg-white/20 transition-colors duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-mail-line text-lg sm:text-xl" />
                E-posta Gönderin
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;